import { genId } from '~/lib/utils';
import type { KeyedRegion } from '~/types/pipeline';

export type BrushStroke = {
  seed: { x: number; y: number };
  radius: number;
  /** 0 = hard edge, 1 = falloff from center. Controls feather width. */
  softness: number;
  stamps: Map<number, number>;
  /** image width — needed for coord encoding */
  w: number;
  prev: { x: number; y: number } | null;
};

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function stampCircle(stroke: BrushStroke, cx: number, cy: number, iw: number, ih: number): void {
  const { radius, softness, stamps, w } = stroke;
  const r2 = radius * radius;
  const rInner = radius * (1 - softness);
  const rInner2 = rInner * rInner;
  const x0 = Math.max(0, Math.floor(cx - radius));
  const x1 = Math.min(iw - 1, Math.ceil(cx + radius));
  const y0 = Math.max(0, Math.floor(cy - radius));
  const y1 = Math.min(ih - 1, Math.ceil(cy + radius));
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const d2 = dx * dx + dy * dy;
      if (d2 > r2) continue;
      const key = y * w + x;
      const prev = stamps.get(key) ?? 0;
      if (prev === 255) continue;
      if (d2 <= rInner2) {
        stamps.set(key, 255);
        continue;
      }
      // softness=0 → rInner=radius → divisor=0; the d2 ≤ r2 guard above means
      // we only reach this branch when softness > 0, so radius - rInner > 0.
      const d = Math.sqrt(d2);
      const alpha = Math.round(255 * smoothstep(1 - (d - rInner) / (radius - rInner)));
      if (alpha > prev) stamps.set(key, alpha);
    }
  }
}

export function startBrushStroke(
  seed: { x: number; y: number },
  radius: number,
  softness: number,
  w: number,
): BrushStroke {
  return { seed, radius, softness, stamps: new Map(), w, prev: null };
}

export function extendBrushStroke(
  stroke: BrushStroke,
  p: { x: number; y: number },
  dims: { w: number; h: number },
): void {
  const prev = stroke.prev ?? p;
  const dx = p.x - prev.x;
  const dy = p.y - prev.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const step = Math.max(0.5, stroke.radius * 0.25);
  const steps = dist < step ? 1 : Math.ceil(dist / step);
  for (let i = 0; i <= steps; i++) {
    stampCircle(stroke, prev.x + dx * (i / steps), prev.y + dy * (i / steps), dims.w, dims.h);
  }
  stroke.prev = p;
}

export function subtractStrokeFromRegions(
  regions: KeyedRegion[],
  stroke: BrushStroke,
): KeyedRegion[] {
  const { stamps, w: imgW } = stroke;
  if (stamps.size === 0) return regions;

  let sxMin = Infinity,
    sxMax = -Infinity,
    syMin = Infinity,
    syMax = -Infinity;
  for (const key of stamps.keys()) {
    const x = key % imgW;
    const y = (key - x) / imgW;
    if (x < sxMin) sxMin = x;
    if (x > sxMax) sxMax = x;
    if (y < syMin) syMin = y;
    if (y > syMax) syMax = y;
  }

  let anyChanged = false;
  const next: KeyedRegion[] = [];
  for (const r of regions) {
    const { bbox, mask } = r;
    const ox0 = Math.max(bbox.x, sxMin);
    const oy0 = Math.max(bbox.y, syMin);
    const ox1 = Math.min(bbox.x + bbox.w - 1, sxMax);
    const oy1 = Math.min(bbox.y + bbox.h - 1, syMax);
    if (ox0 > ox1 || oy0 > oy1) {
      next.push(r);
      continue;
    }

    let changed = false;
    let pixelCount = r.pixelCount;
    let newMask: Uint8Array | null = null;
    for (let y = oy0; y <= oy1; y++) {
      for (let x = ox0; x <= ox1; x++) {
        const sub = stamps.get(y * imgW + x) ?? 0;
        if (sub === 0) continue;
        const li = (y - bbox.y) * bbox.w + (x - bbox.x);
        const v = mask[li] ?? 0;
        if (v === 0) continue;
        const nv = sub >= v ? 0 : v - sub;
        if (nv === v) continue;
        if (!newMask) newMask = new Uint8Array(mask);
        newMask[li] = nv;
        changed = true;
        if (nv === 0) pixelCount--;
      }
    }
    if (!changed) {
      next.push(r);
      continue;
    }
    anyChanged = true;
    if (pixelCount === 0) continue;
    next.push({ ...r, mask: newMask ?? mask, pixelCount });
  }
  return anyChanged ? next : regions;
}

export function finalizeBrushStroke(
  stroke: BrushStroke,
  dims: { w: number; h: number },
  generateId: () => string = genId,
): KeyedRegion {
  const { stamps, seed, w } = stroke;
  let xMin = Infinity,
    xMax = -Infinity,
    yMin = Infinity,
    yMax = -Infinity;
  for (const key of stamps.keys()) {
    const x = key % w;
    const y = (key - x) / w;
    if (x < xMin) xMin = x;
    if (x > xMax) xMax = x;
    if (y < yMin) yMin = y;
    if (y > yMax) yMax = y;
  }
  if (stamps.size === 0) {
    return {
      id: generateId(),
      bbox: { x: Math.round(seed.x), y: Math.round(seed.y), w: 1, h: 1 },
      mask: new Uint8Array(1),
      pixelCount: 0,
      seed,
      tolerance: 0,
    };
  }
  const bw = xMax - xMin + 1;
  const bh = yMax - yMin + 1;
  const mask = new Uint8Array(bw * bh);
  for (const [key, alpha] of stamps) {
    const x = key % w;
    const y = (key - x) / w;
    if (x < 0 || x >= dims.w || y < 0 || y >= dims.h) continue;
    mask[(y - yMin) * bw + (x - xMin)] = alpha;
  }
  return {
    id: generateId(),
    bbox: { x: xMin, y: yMin, w: bw, h: bh },
    mask,
    pixelCount: stamps.size,
    seed,
    tolerance: 0,
  };
}
